/* Logging a walk-in nobody could take.
 *
 * The turn board only records work that happened, so a busy afternoon where
 * four parties left looks exactly like a quiet one. This is the other half of
 * the day: how many people, what they were after, and when they gave up.
 *
 * Deliberately two questions and a button — it gets filled in at the desk with
 * somebody already walking out of the door. */
import * as Vue from 'vue';
import { store } from '../store.js';

export const MissedWalkin = {
  name: 'MissedWalkin',
  setup: function () {
    var m = Vue.computed(function () { return store.ui.missedWalkin; });

    var partyLabel = Vue.computed(function () {
      if (!m.value) return '';
      return m.value.partySize === 1 ? '1 person' : m.value.partySize + ' people';
    });

    // Whole-label interpolation: the compiler eats the space between literal
    // text and a {{ }} and it reads "Log2".
    var saveLabel = Vue.computed(function () {
      if (!m.value) return 'Log it';
      return m.value.partySize === 1 ? 'Log 1 missed' : 'Log ' + m.value.partySize + ' missed';
    });

    // Today's entries, newest first, so a mistake can be taken back straight
    // away rather than needing the database.
    var todayLog = Vue.computed(function () {
      return store.missed.rows.slice().sort(function (a, b) {
        return b.createdAt - a.createdAt;
      });
    });

    function nameOf(id) {
      var svc = store.serviceById.value[id];
      return svc ? svc.name : 'Removed service';
    }

    return {
      m: m,
      partyLabel: partyLabel,
      saveLabel: saveLabel,
      todayLog: todayLog,
      nameOf: nameOf,
      services: store.activeServices,
      est: store.missedWalkinEstimate,
      peopleToday: store.missedTodayPeople,
      setParty: store.setMissedParty,
      toggleService: store.toggleMissedService,
      undo: store.undoMissedWalkin,
      formatClock: store.formatClock,
      waitLabel: store.missedWaitLabel,
      save: store.logMissedWalkin,
      close: store.closeMissedWalkin
    };
  },
  template: [
    '<div v-if="m" class="modal-scrim" @click.self="close">',
    '  <div class="sheet sheet--wide" role="dialog" aria-modal="true">',
    '    <div class="sheet__head sheet__head--plain">',
    '      <div>',
    '        <h3>Missed walk-in</h3>',
    '        <p>Somebody came in and left without being seen.</p>',
    '      </div>',
    '    </div>',
    '',
    '    <div class="assign__section">',
    '      <h4 class="assign__label">How many people?</h4>',
    '      <div class="party">',
    '        <button type="button" class="party__step" :disabled="m.partySize <= 1"',
    '          @click="setParty(m.partySize - 1)" aria-label="One fewer">&minus;</button>',
    '        <input class="input party__n" type="number" min="1" max="20"',
    '          :value="m.partySize" @input="setParty($event.target.value)"',
    '          aria-label="Number of people" />',
    '        <button type="button" class="party__step" :disabled="m.partySize >= 20"',
    '          @click="setParty(m.partySize + 1)" aria-label="One more">+</button>',
    '        <span class="party__label">{{ partyLabel }}</span>',
    '      </div>',
    '    </div>',
    '',
    '    <div class="assign__section">',
    '      <h4 class="assign__label">What did they want?</h4>',
    '      <div class="svc-toggles">',
    '        <button v-for="svc in services" :key="svc.id" type="button" class="chip chip--sm"',
    '          :class="{ \'chip--on\': m.serviceIds.includes(svc.id) }"',
    '          :style="{ \'--chip\': svc.color }" @click="toggleService(svc.id)">',
    '          <span class="chip__letter">{{ svc.abbrev }}</span>',
    '          <span class="chip__name">{{ svc.name }}</span>',
    '        </button>',
    '      </div>',
    '      <p class="setting__hint">Leave it blank if they did not say &mdash; the count still matters.</p>',
    '    </div>',
    '',
    '    <div v-if="todayLog.length" class="assign__section">',
    '      <h4 class="assign__label">Already logged today &mdash; {{ peopleToday }} missed</h4>',
    '      <ul class="missed-log">',
    '        <li v-for="row in todayLog" :key="row.id">',
    '          <span class="missed-log__time">{{ formatClock(row.createdAt) }}</span>',
    '          <span class="missed-log__main">',
    '            <span class="missed-log__n">{{ row.partySize === 1 ? "1 person" : row.partySize + " people" }}</span>',
    '            <span class="missed-log__svc">',
    '              {{ row.serviceIds.length ? row.serviceIds.map(nameOf).join(", ") : "no service given" }}</span>',
    '          </span>',
    /* What they were told at the door, as stamped on the row — not worked out
       again now, when the squares it came from have moved on. */
    '          <span class="missed-log__wait" :class="{ \'is-none\': row.waitMinutes == null }">',
    '            {{ waitLabel(row.waitMinutes) }}</span>',
    '          <button type="button" class="link-btn" @click="undo(row.id)">Remove</button>',
    '        </li>',
    '      </ul>',
    '    </div>',
    '',
    /* Shown rather than merely recorded, because the desk is the only one who
       can see it is wrong — a tech in the back who never got marked in, or an
       estimate resting on a square that finished ten minutes ago and nobody
       stopped. Both move while this is open, so it is read live. */
    '    <p v-if="est" class="setting__hint missed-est">Also logged:',
    '      {{ est.staffPresent }} {{ est.staffPresent === 1 ? "tech" : "techs" }} in,',
    /* Only when they named something. With nothing named this counts everybody
       who does anything, which is the number above it again — and a repeated
       number reads as the board having made a mistake. */
    '      <template v-if="!est.anyService && est.ableByService.length < 2">{{ est.staffAble }} of them',
    '        {{ est.staffAble === 1 ? "does" : "do" }} that work,</template>',
    /* Two or more named: how many could do each, since the union of an acrylic
       and a gel pedicure hides which one was short. */
    '      <template v-else-if="!est.anyService"><template v-for="b in est.ableByService" :key="b.serviceId">',
    '        {{ b.able }} {{ b.able === 1 ? "does" : "do" }} {{ b.name }},</template></template>',
    '      {{ est.waitLabel }}.</p>',
    '',
    '    <div class="sheet__actions">',
    '      <button type="button" class="btn btn--primary btn--lg" @click="save()">{{ saveLabel }}</button>',
    '      <button type="button" class="btn btn--quiet" @click="close">Cancel</button>',
    '    </div>',
    '  </div>',
    '</div>'
  ].join('\n')
};
